import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  TextInput,
  Select,
  Button,
  Group,
  Stack,
  Grid,
  Text,
} from "@mantine/core";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";

// A lead's full name lives in `first_name` (v2 leaves `last_name` null), so
// split off the last word as the last name, the same as iOS.
const splitName = lead => {
  const full = [lead.first_name, lead.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const parts = full.split(/\s+/);
  if (parts.length < 2) return { firstName: full, lastName: "" };
  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
};

// "(555) 123-4567" as the digits are typed, like iOS's phone field
const formatPhone = raw => {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// Mantine's Select throws on a repeated value, and the API can list the same
// person twice
export const uniqueOptions = options => {
  const seen = new Set();
  return options.filter(o => !seen.has(o.value) && seen.add(o.value));
};

export const leadName = lead =>
  [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim();

// The new user's role on POST /v2/rep, the same choices as iOS's User Type
const USER_TYPES = [
  { value: "rep", label: "Rep" },
  { value: "manager", label: "Manager" },
  { value: "company_admin", label: "Company Admin" },
];

const EMPTY_FORM = {
  leadId: null,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  managerUserId: null,
  recruitedByUserId: null,
  repTypeId: null,
  recruitingSourceId: null,
  userType: "rep",
};

/// The Leads tab's Add Rep: the web version of iOS's Send Offer step 0 "Add
/// Manually". Creates the rep with POST /v2/rep; when it's opened from a lead
/// the API converts that lead in place.
function AddRepFromLeadModal({ opened, lead, onClose, onAdded }) {
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess } = useToast();

  const [form, setForm] = useState(EMPTY_FORM);
  // Downline plus the caller (everyone for a company admin), keyed by user id
  const [people, setPeople] = useState([]);
  const [repTypes, setRepTypes] = useState([]);
  const [recruitingSources, setRecruitingSources] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const fillFromLead = picked => {
    const { firstName, lastName } = splitName(picked);
    setForm(prev => ({
      ...prev,
      leadId: picked.id,
      firstName,
      lastName,
      email: picked.email || "",
      phone: formatPhone(picked.phone_number || ""),
    }));
  };

  useEffect(() => {
    if (!opened) return;
    setForm(EMPTY_FORM);
    setError(null);
    if (lead) fillFromLead(lead);

    let cancelled = false;
    const load = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        };
        const [peopleRes, typesRes, sourcesRes] = await Promise.all([
          fetch(
            getApiEndpoint(`/v2/company-downline/downline/rep/${user.repId}`),
            { headers }
          ),
          fetch(getApiEndpoint("/v2/company/rep-types"), { headers }),
          fetch(getApiEndpoint("/v2/company/recruiting-sources"), { headers }),
        ]);
        if (cancelled) return;

        if (peopleRes.ok) {
          const users = ((await peopleRes.json()).data || []).filter(
            u => u.rep_id != null
          );
          if (cancelled) return;
          setPeople(users);
          // Manager and Recruited By default to whoever is adding the rep
          const me = users.find(u => Number(u.rep_id) === Number(user.repId));
          if (me) {
            setForm(prev => ({
              ...prev,
              managerUserId: prev.managerUserId ?? String(me.id),
              recruitedByUserId: prev.recruitedByUserId ?? String(me.id),
            }));
          }
        }
        if (typesRes.ok) {
          const types = (await typesRes.json()).data || [];
          if (!cancelled) setRepTypes(types);
        }
        if (sourcesRes.ok) {
          const sources = (await sourcesRes.json()).data || [];
          if (!cancelled) setRecruitingSources(sources);
        }
      } catch {
        // Dropdowns are best-effort; the form still says what's missing
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, lead]);

  const personName = p => `${p.first_name || ""} ${p.last_name || ""}`.trim();

  const managerOptions = useMemo(
    () =>
      uniqueOptions(
        people
          .filter(p =>
            ["MANAGER", "COMPANY_ADMIN"].includes(
              (p.role_code || "").toUpperCase()
            )
          )
          .map(p => ({ value: String(p.id), label: personName(p) }))
      ),
    [people]
  );
  const recruiterOptions = useMemo(
    () =>
      uniqueOptions(
        people.map(p => ({ value: String(p.id), label: personName(p) }))
      ),
    [people]
  );

  // Company Admin is only offered to company admins; the API rejects it from
  // anyone else
  const userTypeOptions = USER_TYPES.filter(
    type => type.value !== "company_admin" || user?.role === "COMPANY_ADMIN"
  );

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSave =
    form.firstName.trim() &&
    form.lastName.trim() &&
    emailValid &&
    form.managerUserId &&
    form.recruitedByUserId &&
    form.repTypeId &&
    form.userType &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const body = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        recruited_by_user_id: Number(form.recruitedByUserId),
        manager_user_id: Number(form.managerUserId),
        rep_type_id: Number(form.repTypeId),
        is_manager: form.userType === "manager",
        ...(form.userType === "company_admin" && { is_company_admin: true }),
        ...(form.recruitingSourceId && {
          recruiting_source_id: Number(form.recruitingSourceId),
        }),
        ...(form.leadId && { lead_id: form.leadId }),
      };
      const response = await fetch(getApiEndpoint("/v2/rep"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let message = "Failed to add rep";
        try {
          const data = await response.json();
          const raw = data.message ?? data.error;
          if (raw) message = Array.isArray(raw) ? raw.join(", ") : raw;
        } catch {
          // keep the default
        }
        throw new Error(message);
      }
      showSuccess(`${body.first_name} ${body.last_name} added as a rep`);
      onAdded?.();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to add rep");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add Rep" centered size="lg">
      <Stack gap="md">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="First name"
              placeholder="First name"
              value={form.firstName}
              onChange={e => set("firstName", e.currentTarget.value)}
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Last name"
              placeholder="Last name"
              value={form.lastName}
              onChange={e => set("lastName", e.currentTarget.value)}
              required
              disabled={saving}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              type="email"
              label="Email"
              placeholder="name@email.com"
              value={form.email}
              onChange={e => set("email", e.currentTarget.value)}
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              type="tel"
              label="Phone"
              placeholder="(000) 000-0000"
              value={form.phone}
              onChange={e => set("phone", formatPhone(e.currentTarget.value))}
              disabled={saving}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Manager"
              placeholder="Select manager"
              data={managerOptions}
              value={form.managerUserId}
              onChange={value => set("managerUserId", value)}
              searchable
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="User type"
              data={userTypeOptions}
              value={form.userType}
              onChange={value => set("userType", value)}
              allowDeselect={false}
              required
              disabled={saving}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Recruited by"
              placeholder="Select rep"
              data={recruiterOptions}
              value={form.recruitedByUserId}
              onChange={value => set("recruitedByUserId", value)}
              searchable
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Rep type"
              placeholder="Select type"
              data={uniqueOptions(
                repTypes.map(t => ({ value: String(t.id), label: t.name }))
              )}
              value={form.repTypeId}
              onChange={value => set("repTypeId", value)}
              searchable
              required
              disabled={saving}
            />
          </Grid.Col>
        </Grid>

        <Select
          label="Recruiting source"
          placeholder="Select source"
          data={uniqueOptions(
            recruitingSources.map(s => ({
              value: String(s.id),
              label: s.source_name,
            }))
          )}
          value={form.recruitingSourceId}
          onChange={value => set("recruitingSourceId", value)}
          searchable
          clearable
          disabled={saving}
        />

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Add Rep
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddRepFromLeadModal;
